import { Check, Mail, UserRound } from "lucide-react";
import type { PaymentDetailsValidity } from "../ownership/paymentDetails";

type PaymentDetailsProgressProps = {
  validity: PaymentDetailsValidity;
};

const progressItems = [
  { key: "givenName", label: "First name", Icon: UserRound },
  { key: "familyName", label: "Last name", Icon: UserRound },
  { key: "email", label: "Contact email", Icon: Mail },
] as const;

export default function PaymentDetailsProgress({
  validity,
}: PaymentDetailsProgressProps) {
  return (
    <section
      aria-live="polite"
      aria-label="Payment details progress"
      className="overflow-hidden rounded-xl border border-brand-neutral-700 bg-brand-neutral-800 shadow-sm"
    >
      <div className="p-4 sm:p-5">
        <h3 className="text-base font-semibold text-foreground">
          Complete your details to continue
        </h3>
        <p className="mt-1.5 text-sm text-brand-neutral-500">
          Add your name and contact email on the left to unlock your payment options.
        </p>
      </div>

      <div className="grid gap-4 border-t border-brand-neutral-700/80 bg-brand-neutral-900/60 px-4 py-4 sm:grid-cols-3 sm:gap-3 sm:px-5">
        {progressItems.map(({ key, label, Icon }) => {
          const isComplete = validity[key];

          return (
            <div
              key={key}
              aria-label={`${label}: ${isComplete ? "Thanks" : "Required"}`}
              className="flex items-center gap-3"
            >
              <div
                aria-hidden="true"
                className={
                  isComplete
                    ? "flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400"
                    : "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-brand-orange-500/80 text-brand-orange-500"
                }
              >
                {isComplete ? (
                  <Check className="h-4 w-4" strokeWidth={2.5} />
                ) : (
                  <Icon className="h-4 w-4" strokeWidth={1.8} />
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{label}</p>
                <p
                  className={
                    isComplete
                      ? "text-xs text-emerald-400"
                      : "text-xs text-brand-orange-500"
                  }
                >
                  {isComplete ? "Thanks" : "Required"}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
